/**
 * LocationService - Unified location management for SuperGold Rails
 * 
 * Provides shared location functionality across Stimulus controllers and Vue.js components
 */

import { LOCATION_TEXT, LOCATION_CONFIG } from '../constants/location.js';

// Re-export constants for backward compatibility
export const CONFIG = LOCATION_CONFIG;
export const MESSAGES = {
  ALL_NZ: LOCATION_TEXT.DEFAULT,
  SELECTED_LOCATION: LOCATION_TEXT.SELECTED_LOCATION,
};

export class LocationService {
  /**
   * Get current location data from URL parameters
   */
  static getLocationFromURL() {
    const params = new URLSearchParams(window.location.search);
    return {
      address: params.get('location[address]'),
      radius: params.get('location[radius]'),
      lat: params.get('location[lat]'),
      lng: params.get('location[lng]'),
    };
  }

  /**
   * Load location data from localStorage
   */
  static loadFromStorage() {
    try {
      const stored = localStorage.getItem(CONFIG.STORAGE_KEY);
      if (!stored) return null;

      const locationData = JSON.parse(stored);
      
      // Check if data is expired
      if (Date.now() - locationData.timestamp > CONFIG.STORAGE_EXPIRY_MS) {
        localStorage.removeItem(CONFIG.STORAGE_KEY);
        return null;
      }

      return locationData;
    } catch {
      localStorage.removeItem(CONFIG.STORAGE_KEY);
      return null;
    }
  }

  /**
   * Save location data to localStorage
   */
  static saveToStorage(locationData) {
    if (!locationData) return;

    const dataToStore = {
      ...locationData,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (error) {
      console.warn('Failed to save location to localStorage:', error);
    }
  }

  /**
   * Get the current location for display purposes
   */
  static getCurrentLocationData() {
    // First try URL parameters
    const urlParams = this.getLocationFromURL();
    if (urlParams.address || (urlParams.lat && urlParams.lng)) {
      return {
        address: urlParams.address,
        lat: urlParams.lat ? parseFloat(urlParams.lat) : null,
        lng: urlParams.lng ? parseFloat(urlParams.lng) : null,
        radius: urlParams.radius ? parseInt(urlParams.radius) : CONFIG.DEFAULT_RADIUS_KM,
        source: 'url',
      };
    }

    // Fallback to localStorage
    const storedData = this.loadFromStorage();
    if (storedData) {
      return {
        address: storedData.address,
        lat: storedData.lat,
        lng: storedData.lng,
        radius: storedData.radius || CONFIG.DEFAULT_RADIUS_KM,
        source: 'storage',
      };
    }

    return null;
  }

  /**
   * Get formatted display text for the current location
   */
  static getLocationDisplayText() {
    const locationData = this.getCurrentLocationData();
    
    if (!locationData) {
      return MESSAGES.ALL_NZ;
    }

    const { address, radius, lat, lng } = locationData;
    
    if (address && radius) {
      const shortAddress = this.truncateAddress(address);
      return `${radius}km of ${shortAddress}`;
    }
    
    if (lat && lng && radius) {
      return `${radius}km of ${MESSAGES.SELECTED_LOCATION}`;
    }

    return MESSAGES.ALL_NZ;
  }

  /**
   * Truncate address for display
   */
  static truncateAddress(address) {
    if (!address || address.length <= CONFIG.ADDRESS_MAX_LENGTH) {
      return address;
    }
    return `${address.substring(0, CONFIG.ADDRESS_MAX_LENGTH)}...`;
  }

  /**
   * Clear stored location data
   */
  static clearStorage() {
    localStorage.removeItem(CONFIG.STORAGE_KEY);
  }

  /**
   * Dispatch a custom event when location changes
   */
  static dispatchLocationChange(locationData = null) {
    const event = new CustomEvent('location:changed', {
      detail: {
        locationData: locationData || this.getCurrentLocationData(),
        displayText: this.getLocationDisplayText(),
      },
      bubbles: true,
    });

    document.dispatchEvent(event);
  }

  /**
   * Format coordinates for display
   */
  static formatCoordinates(lat, lng) {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }

  /**
   * Parse location parameters from URL search params
   */
  static parseLocationParams(params) {
    const lat = params.get('location[lat]');
    const lng = params.get('location[lng]');
    
    if (!lat || !lng) return null;

    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);

    return {
      lat: parsedLat,
      lng: parsedLng,
      address: params.get('location[address]') || this.formatCoordinates(parsedLat, parsedLng),
      radius: params.get('location[radius]') ? parseInt(params.get('location[radius]')) : CONFIG.DEFAULT_RADIUS_KM,
    };
  }

  /**
   * Update URL with location parameters
   */
  static updateURL(locationData) {
    if (!locationData) return;

    const url = new URL(window.location);
    const params = url.searchParams;

    // Clear old region/district parameters
    params.delete('menu[region]');
    params.delete('menu[district]');

    // Set location parameters
    if (locationData.lat && locationData.lng) {
      params.set('location[lat]', locationData.lat.toString());
      params.set('location[lng]', locationData.lng.toString());
    }

    if (locationData.radius) {
      params.set('location[radius]', locationData.radius.toString());
    }
    
    if (locationData.address) {
      params.set('location[address]', locationData.address);
    }

    return url.toString();
  }

  /**
   * Check if location data is valid
   */
  static isValidLocationData(data) {
    return data && (
      (data.lat && data.lng) || 
      data.address
    );
  }

  /**
   * Get default location (Auckland center)
   */
  static getDefaultLocation() {
    return {
      lat: -36.8485,
      lng: 174.7633,
      address: 'Auckland, New Zealand',
      radius: CONFIG.DEFAULT_RADIUS_KM,
    };
  }
}

// Export for direct usage
export default LocationService;